// My To-Dos — cross-project to-do list with status and priority filters
import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useQueries } from '@tanstack/react-query'
import { ArrowLeft, CheckSquare, Plus, X } from 'lucide-react'
import { getCrmTodos, createCrmTodo, updateCrmTodo, deleteCrmTodo, getProjectOwners } from '../../../services/api'
import { useAuth } from '../../../contexts/AuthContext'
import type { CrmTodo, TodoStatus, TodoPriority } from '../../../types'
import PageHeader from '../../../components/ui/PageHeader'
import EmptyState from '../../../components/ui/EmptyState'
import ConfirmDialog from '../../../components/ui/ConfirmDialog'

const STATUS_BADGE: Record<TodoStatus, string> = {
  open: 'bg-gray-500/20 text-gray-600',
  in_progress: 'bg-blue-500/20 text-blue-700',
  completed: 'bg-green-500/20 text-green-700',
  cancelled: 'bg-red-500/20 text-red-600',
  suggested: 'bg-purple-500/20 text-purple-700',
  // BUG: whatnext/handler.py writes real todos with status='pending' (AI/workflow
  // suggestions accepted from the What Next panel — e.g. "Start Patent Filing").
  // That value has no key in this Record, so `STATUS_BADGE[t.status]` was
  // `undefined` and the whole Status cell rendered blank for every such row —
  // real data silently disappearing rather than a missing-data honest state.
  pending: 'bg-indigo-500/20 text-indigo-700',
}

const PRIORITY_BADGE: Record<TodoPriority, string> = {
  low: 'bg-gray-500/20 text-gray-500',
  medium: 'bg-yellow-500/20 text-yellow-700',
  high: 'bg-orange-500/20 text-orange-700',
  urgent: 'bg-red-500/20 text-red-700',
}

const STATUS_LABELS: Record<TodoStatus, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
  suggested: 'Suggested',
  pending: 'Pending',
}

const PRIORITY_LABELS: Record<TodoPriority, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent',
}

// 'pending' included alongside the user-settable statuses so a to-do the
// backend created with that real status can actually be found by the filter,
// not just displayed once it happens to be in the unfiltered list.
const ALL_STATUSES: TodoStatus[] = ['open', 'in_progress', 'pending', 'completed', 'cancelled']
const ALL_PRIORITIES: TodoPriority[] = ['low', 'medium', 'high', 'urgent']

export default function MyTodos({ embedded = false }: { embedded?: boolean } = {}) {
  const { user } = useAuth()
  const [todos, setTodos] = useState<CrmTodo[]>([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState<TodoStatus | ''>('')
  const [priorityFilter, setPriorityFilter] = useState<TodoPriority | ''>('')
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [actionError, setActionError] = useState('')
  const [pendingDelete, setPendingDelete] = useState<CrmTodo | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingEntityTypeId, setEditingEntityTypeId] = useState('')
  const [formTitle, setFormTitle] = useState('')
  const [formDescription, setFormDescription] = useState('')
  const [formPriority, setFormPriority] = useState<TodoPriority>('medium')
  const [formDueDate, setFormDueDate] = useState('')

  useEffect(() => {
    setLoading(true)
    getCrmTodos()
      .then((data) => setTodos(data.todos || []))
      .catch(() => setTodos([]))
      .finally(() => setLoading(false))
  }, [])

  const resetForm = () => {
    setFormTitle('')
    setFormDescription('')
    setFormPriority('medium')
    setFormDueDate('')
    setEditingId(null)
    setEditingEntityTypeId('')
    setError('')
    setShowForm(false)
  }

  const handleSave = async () => {
    if (!formTitle.trim()) return
    setSaving(true)
    setError('')
    try {
      if (editingId) {
        const updated = await updateCrmTodo(editingEntityTypeId, editingId, {
          title: formTitle.trim(),
          description: formDescription.trim(),
          priority: formPriority,
          due_date: formDueDate || undefined,
        })
        setTodos((prev) => prev.map((t) => t.todo_id === editingId ? { ...t, ...updated } : t))
      } else {
        const created = await createCrmTodo({
          entity_type_id: 'user#general',
          title: formTitle.trim(),
          description: formDescription.trim(),
          priority: formPriority,
          due_date: formDueDate || undefined,
        })
        setTodos((prev) => [created, ...prev])
      }
      resetForm()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save to-do')
    } finally {
      setSaving(false)
    }
  }

  const handleEdit = (t: CrmTodo) => {
    setEditingId(t.todo_id)
    setEditingEntityTypeId(t.entity_type_id)
    setFormTitle(t.title)
    setFormDescription(t.description || '')
    setFormPriority(t.priority)
    setFormDueDate(t.due_date || '')
    setError('')
    setShowForm(true)
  }

  // Honest delete (P7): the real mutation runs inside the ConfirmDialog. On
  // failure it throws so the dialog stays open and surfaces the message.
  const confirmDelete = async () => {
    if (!pendingDelete) return
    await deleteCrmTodo(pendingDelete.entity_type_id, pendingDelete.todo_id)
    setTodos((prev) => prev.filter((t) => t.todo_id !== pendingDelete.todo_id))
  }

  const handleToggleStatus = async (t: CrmTodo) => {
    const newStatus: TodoStatus = t.status === 'completed' ? 'open' : 'completed'
    setActionError('')
    try {
      await updateCrmTodo(t.entity_type_id, t.todo_id, { status: newStatus })
      setTodos((prev) => prev.map((item) => item.todo_id === t.todo_id ? { ...item, status: newStatus } : item))
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to update to-do')
    }
  }

  const filtered = todos.filter((t) => {
    if (statusFilter && t.status !== statusFilter) return false
    if (priorityFilter && t.priority !== priorityFilter) return false
    return true
  })

  // Real assignee resolution: CrmTodo.assignee_id is a bare user_id, which
  // rendered as an unreadable raw UUID. Resolve it to a real email via each
  // referenced project's actual owner list (getProjectOwners — the same real
  // endpoint SharedProjects already uses for its Owner column), falling back
  // to "You" when the assignee is the signed-in user. When no project-owner
  // record matches, the honest fallback is the id itself (never a fabricated
  // name), just shortened so the table stays readable.
  const assigneeProjectIds = [...new Set(
    todos
      .filter((t) => t.assignee_id && t.entity_type_id.startsWith('project#'))
      .map((t) => t.entity_type_id.slice('project#'.length))
  )]
  const ownerQueries = useQueries({
    queries: assigneeProjectIds.map((projectId) => ({
      queryKey: ['todo-assignee-project-owners', projectId],
      queryFn: () => getProjectOwners(projectId),
      staleTime: 5 * 60 * 1000,
    })),
  })
  const assigneeEmailById: Record<string, string> = {}
  ownerQueries.forEach((q) => {
    (q.data || []).forEach((owner) => { assigneeEmailById[owner.user_id] = owner.email })
  })
  const resolveAssignee = (assigneeId: string | undefined): { label: string; resolved: boolean } | null => {
    if (!assigneeId) return null
    if (user && assigneeId === user.id) return { label: 'You', resolved: true }
    const email = assigneeEmailById[assigneeId]
    if (email) return { label: email, resolved: true }
    return { label: `${assigneeId.slice(0, 8)}…`, resolved: false }
  }

  const hasFilter = !!(statusFilter || priorityFilter)

  return (
    <div className="space-y-4">
      {!embedded && (
        <Link to="/crm" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
          <ArrowLeft size={16} /> Back to CRM
        </Link>
      )}

      <PageHeader
        title={embedded ? 'To-Dos' : 'My To-Dos'}
        description="Follow-ups and tasks across all your projects — filter by status or priority, and mark them done."
        helpKey="crm"
        status={{ label: `(${filtered.length})` }}
        primaryAction={
          <button
            onClick={() => { resetForm(); setShowForm(true) }}
            className="flex items-center gap-1.5 px-3 py-2 bg-sky-500 text-white text-sm font-medium rounded-lg hover:bg-sky-600 transition-colors"
          >
            <Plus size={16} /> Add To-Do
          </button>
        }
      />

      {/* Action error (status toggle) */}
      {actionError && (
        <div className="flex items-center justify-between gap-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
          <span>{actionError}</span>
          <button onClick={() => setActionError('')} className="text-red-400 hover:text-red-600 shrink-0"><X size={16} /></button>
        </div>
      )}

      {/* Inline form */}
      {showForm && (
        <div className="light-card p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700">{editingId ? 'Edit To-Do' : 'New To-Do'}</h3>
            <button onClick={resetForm} className="text-gray-500 hover:text-gray-600"><X size={18} /></button>
          </div>
          {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</p>}
          <input
            type="text"
            placeholder="Title *"
            value={formTitle}
            onChange={(e) => setFormTitle(e.target.value)}
            className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
          />
          <textarea
            placeholder="Description (optional)"
            value={formDescription}
            onChange={(e) => setFormDescription(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
          />
          <div className="flex gap-3">
            <select
              value={formPriority}
              onChange={(e) => setFormPriority(e.target.value as TodoPriority)}
              className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 bg-white text-gray-700"
            >
              {ALL_PRIORITIES.map((p) => (
                <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>
              ))}
            </select>
            <input
              type="date"
              value={formDueDate}
              onChange={(e) => setFormDueDate(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={resetForm} className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700">Cancel</button>
            <button
              onClick={handleSave}
              disabled={!formTitle.trim() || saving}
              className="px-4 py-2 bg-sky-500 text-white text-sm font-medium rounded-lg hover:bg-sky-600 disabled:opacity-50 transition-colors"
            >
              {saving ? 'Saving...' : editingId ? 'Update' : 'Create'}
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as TodoStatus | '')}
          className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 bg-white text-gray-700"
        >
          <option value="">All Statuses</option>
          {ALL_STATUSES.map((s) => (
            <option key={s} value={s}>{STATUS_LABELS[s]}</option>
          ))}
        </select>

        <select
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value as TodoPriority | '')}
          className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 bg-white text-gray-700"
        >
          <option value="">All Priorities</option>
          {ALL_PRIORITIES.map((p) => (
            <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>
          ))}
        </select>
      </div>

      {/* Content */}
      {loading ? (
        <div className="animate-pulse space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-16 bg-sky-500/10 rounded-lg" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={CheckSquare}
          title={hasFilter ? 'No to-dos match your filters' : 'No to-dos yet'}
          description={hasFilter
            ? 'Try clearing a status or priority filter.'
            : 'Add a to-do to track follow-ups across all your projects.'}
          action={hasFilter ? undefined : (
            <button
              onClick={() => { resetForm(); setShowForm(true) }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-sky-500 text-white text-sm font-medium rounded-lg hover:bg-sky-600"
            >
              <Plus size={16} /> Add your first to-do
            </button>
          )}
        />
      ) : (
        <div className="light-card overflow-hidden">
          <table className="min-w-full divide-y divide-sky-200/50">
            <thead className="bg-sky-500/5">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assignee</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-sky-200/30">
              {filtered.map((t) => (
                <tr key={t.todo_id} className="hover:bg-white/30">
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium text-gray-900">{t.title}</p>
                    {t.description && (
                      <p className="text-xs text-gray-500 truncate max-w-xs">{t.description}</p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE[t.status]}`}>
                      {STATUS_LABELS[t.status]}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium ${PRIORITY_BADGE[t.priority]}`}>
                      {PRIORITY_LABELS[t.priority]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {/* BUG-158: due_date is a date-only value ("YYYY-MM-DD"). Parsing it
                        with new Date() treats it as UTC midnight, so toLocaleDateString()
                        rolls back a day in negative-offset (western) timezones. Format in
                        UTC so the stored calendar date is shown verbatim. */}
                    {t.due_date ? new Date(t.due_date).toLocaleDateString(undefined, { timeZone: 'UTC' }) : '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {(() => {
                      const assignee = resolveAssignee(t.assignee_id)
                      if (!assignee) return '—'
                      return assignee.resolved ? (
                        <span>{assignee.label}</span>
                      ) : (
                        <span className="font-mono text-xs text-gray-400" title={t.assignee_id}>
                          {assignee.label}
                        </span>
                      )
                    })()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleToggleStatus(t)}
                        title={t.status === 'completed' ? 'Reopen' : 'Complete'}
                        className="text-xs text-gray-500 hover:text-green-600"
                      >
                        {t.status === 'completed' ? 'Reopen' : 'Done'}
                      </button>
                      <button
                        onClick={() => handleEdit(t)}
                        className="text-xs text-gray-500 hover:text-sky-600"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => setPendingDelete(t)}
                        className="text-xs text-gray-500 hover:text-red-600"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        tone="danger"
        title="Delete to-do?"
        message={
          pendingDelete
            ? `This permanently removes "${pendingDelete.title}". This can't be undone.`
            : ''
        }
        confirmLabel="Delete to-do"
        onConfirm={confirmDelete}
        onClose={() => setPendingDelete(null)}
      />
    </div>
  )
}
